import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import Layout from '../components/Layout';
import AutoComplete from '../components/AutoComplete';
import Toast from '../components/Toast';
import {
  kardexService, articuloService, tablaService,
} from '../services/api';
import { getErrorDetail } from '../utils/errorHelper';
import '../styles/datatable.css';

const today  = () => new Date().toISOString().split('T')[0];
const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const fmt  = (v, d = 2) => (parseFloat(v) || 0).toLocaleString('es-PE', { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtQ = (v) => (parseFloat(v) || 0).toLocaleString('es-PE', { maximumFractionDigits: 4 });
const fmtFchFull = (d) => {
  if (!d) return '—';
  const s = String(d);
  const [date, timeRaw] = s.split('T');
  const time = (timeRaw || '').slice(0, 5);
  return time ? `${date} ${time}` : date;
};

const MESES = [
  { v: '',  n: 'Todos' }, { v: '1',  n: 'Enero' }, { v: '2',  n: 'Febrero' },
  { v: '3', n: 'Marzo' }, { v: '4', n: 'Abril' },  { v: '5', n: 'Mayo' },
  { v: '6', n: 'Junio' }, { v: '7', n: 'Julio' },  { v: '8', n: 'Agosto' },
  { v: '9', n: 'Septiembre' }, { v: '10', n: 'Octubre' },
  { v: '11', n: 'Noviembre' }, { v: '12', n: 'Diciembre' },
];

const TIPO_DOC_COLOR = {
  PE: { c: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)' },
  PS: { c: '#fb923c', bg: 'rgba(251,146,60,0.12)', border: 'rgba(251,146,60,0.3)' },
  GS: { c: '#a78bfa', bg: 'rgba(167,139,250,0.12)',border: 'rgba(167,139,250,0.3)' },
};

export default function Kardex({ modo = 'kardex' }) {
  const esConsumo = modo === 'consumo';
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState(null);

  const [articulos,     setArticulos]     = useState([]);
  const [grupos,        setGrupos]        = useState([]);
  const [familias,      setFamilias]      = useState([]);
  const [lineas,        setLineas]        = useState([]);
  const [marcas,        setMarcas]        = useState([]);
  const [modelos,       setModelos]       = useState([]);

  // Filtros
  const [modoFecha, setModoFecha] = useState('rango'); // 'rango' | 'mes' | 'ano' | 'fecha'
  const [filtros, setFiltros] = useState({
    id_articulo: '', id_grupo: '', id_familia: '', id_linea: '', id_marca: '', id_modelo: '',
    fecha_desde: firstOfYear(), fecha_hasta: today(),
    year: String(new Date().getFullYear()), month: '', fecha: today(),
  });
  const setF = (k, v) => setFiltros(prev => ({ ...prev, [k]: v }));

  // Vista: artículos expandidos
  const [expandidos, setExpandidos] = useState({}); // { id_articulo: true }

  /* ── Catálogos ── */
  useEffect(() => {
    const svcGrupo    = tablaService('grupos');
    const svcFamilia  = tablaService('familias');
    const svcLinea    = tablaService('lineas');
    const svcMarca    = tablaService('marcas');
    const svcModelo   = tablaService('modelos');
    Promise.all([
      articuloService.getAll(),
      svcGrupo.getAll(), svcFamilia.getAll(), svcLinea.getAll(),
      svcMarca.getAll(), svcModelo.getAll(),
    ]).then(([a, g, f, l, m, mo]) => {
      setArticulos((a.data || []).map(x => ({
        ...x,
        display_articulo: `[${x.codigo_interno || ''}] ${x.descripcion || ''}`,
      })));
      setGrupos(g.data || []);
      setFamilias(f.data || []);
      setLineas(l.data || []);
      setMarcas(m.data || []);
      setModelos(mo.data || []);
    }).catch(() => setToast({ type: 'error', message: 'Error al cargar catálogos.' }));
  }, []);

  /* ── Consultar kardex ── */
  const consultar = useCallback(async () => {
    setLoading(true);
    const params = {};
    ['id_articulo','id_grupo','id_familia','id_linea','id_marca','id_modelo'].forEach(k => {
      if (filtros[k]) params[k] = filtros[k];
    });
    if (modoFecha === 'rango') {
      if (filtros.fecha_desde) params.fecha_desde = filtros.fecha_desde;
      if (filtros.fecha_hasta) params.fecha_hasta = filtros.fecha_hasta;
    } else if (modoFecha === 'fecha') {
      if (filtros.fecha) params.fecha = filtros.fecha;
    } else if (modoFecha === 'mes') {
      if (filtros.year)  params.year  = filtros.year;
      if (filtros.month) params.month = filtros.month;
    } else if (modoFecha === 'ano') {
      if (filtros.year)  params.year  = filtros.year;
    }
    if (esConsumo) params.cod_mov = 'KS';
    try {
      const r = await kardexService.get(params);
      setData(r.data);
      setExpandidos({});
    } catch (err) {
      setToast({ type: 'error', message: getErrorDetail(err, 'Error al consultar kardex.') });
    } finally { setLoading(false); }
  }, [filtros, modoFecha, esConsumo]);

  useEffect(() => { consultar(); /* carga inicial */ /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  /* ── Expandir/contraer ── */
  const toggleArt = (id) => setExpandidos(prev => ({ ...prev, [id]: !prev[id] }));
  const expandirTodos = () => {
    const m = {};
    (data?.articulos || []).forEach(a => { m[a.id_articulo] = true; });
    setExpandidos(m);
  };
  const contraerTodos = () => setExpandidos({});

  /* ── Export Excel — formato profesional con ExcelJS ── */
  const exportExcel = async () => {
    if (!data?.articulos?.length) {
      setToast({ type: 'warning', message: 'No hay datos para exportar.' });
      return;
    }
    const wb = new ExcelJS.Workbook();
    wb.creator = 'Siscorp ERP';
    wb.created = new Date();

    if (esConsumo) {
      await _buildConsumoSheet(wb, data);
      const buf = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buf], { type: XLSX_MIME }), `reporte_consumo_${today()}.xlsx`);
    } else {
      await _buildKardexSheet(wb, data);
      const buf = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buf], { type: XLSX_MIME }), `kardex_${today()}.xlsx`);
    }
  };

  /* ── Render ── */
  const inp = {
    height: 32, padding: '0 10px', fontSize: 12.5, borderRadius: 7,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: '#e2e8f0', outline: 'none', width: '100%', boxSizing: 'border-box',
  };
  const labelS = { fontSize: 10.5, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 3, display: 'block' };

  return (
    <Layout
      title={esConsumo ? 'Reporte de Consumo' : 'Kardex Valorizado'}
      subtitle={esConsumo ? 'Movimientos KS — Promedio Ponderado' : 'Promedio Ponderado'}
    >
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* ── Filtros ── */}
      <div style={{
        background: 'rgba(15,27,45,0.5)', border: '1px solid rgba(255,255,255,0.07)',
        borderRadius: 12, padding: 14, marginBottom: 14,
      }}>
        <div style={{
          display: 'grid', gap: 10,
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          alignItems: 'end',
        }}>
          {/* Artículo */}
          <div>
            <label style={labelS}>Artículo</label>
            <AutoComplete
              value={filtros.id_articulo} options={articulos}
              idField="id_articulo" displayField="display_articulo"
              selectedField="codigo_interno" placeholder="Todos los artículos..."
              dropdownMinWidth={400}
              onChange={v => setF('id_articulo', v || '')}
            />
          </div>
          <div>
            <label style={labelS}>Grupo</label>
            <AutoComplete value={filtros.id_grupo} options={grupos}
              idField="id_grupo" displayField="descripcion" placeholder="Todos"
              onChange={v => setF('id_grupo', v || '')} />
          </div>
          <div>
            <label style={labelS}>Familia</label>
            <AutoComplete value={filtros.id_familia} options={familias}
              idField="id_familia" displayField="descripcion" placeholder="Todas"
              onChange={v => setF('id_familia', v || '')} />
          </div>
          <div>
            <label style={labelS}>Línea</label>
            <AutoComplete value={filtros.id_linea} options={lineas}
              idField="id_linea" displayField="descripcion" placeholder="Todas"
              onChange={v => setF('id_linea', v || '')} />
          </div>
          <div>
            <label style={labelS}>Marca</label>
            <AutoComplete value={filtros.id_marca} options={marcas}
              idField="id_marca" displayField="descripcion" placeholder="Todas"
              onChange={v => setF('id_marca', v || '')} />
          </div>
          <div>
            <label style={labelS}>Modelo</label>
            <AutoComplete value={filtros.id_modelo} options={modelos}
              idField="id_modelo" displayField="descripcion" placeholder="Todos"
              onChange={v => setF('id_modelo', v || '')} />
          </div>
        </div>

        {/* ── Fechas: selector de modo + campos según modo ── */}
        <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
          <div>
            <label style={labelS}>Filtro de Fecha</label>
            <div style={{ display: 'inline-flex', padding: 2, borderRadius: 8,
              background: 'rgba(15,27,45,0.6)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {[
                { k: 'rango', lbl: 'Rango' },
                { k: 'fecha', lbl: 'Fecha' },
                { k: 'mes',   lbl: 'Mes/Año' },
                { k: 'ano',   lbl: 'Año' },
              ].map(opt => (
                <button key={opt.k} type="button" onClick={() => setModoFecha(opt.k)}
                  style={{
                    padding: '6px 14px', fontSize: 11.5, fontWeight: 600,
                    background: modoFecha === opt.k ? 'rgba(96,165,250,0.2)' : 'transparent',
                    color:      modoFecha === opt.k ? '#93c5fd' : '#64748b',
                    border: 'none', borderRadius: 6, cursor: 'pointer',
                  }}>{opt.lbl}</button>
              ))}
            </div>
          </div>

          {modoFecha === 'rango' && (
            <>
              <div>
                <label style={labelS}>Desde</label>
                <input type="date" style={inp} value={filtros.fecha_desde}
                  onChange={e => setF('fecha_desde', e.target.value)} />
              </div>
              <div>
                <label style={labelS}>Hasta</label>
                <input type="date" style={inp} value={filtros.fecha_hasta}
                  onChange={e => setF('fecha_hasta', e.target.value)} />
              </div>
            </>
          )}

          {modoFecha === 'fecha' && (
            <div>
              <label style={labelS}>Fecha</label>
              <input type="date" style={inp} value={filtros.fecha}
                onChange={e => setF('fecha', e.target.value)} />
            </div>
          )}

          {(modoFecha === 'mes' || modoFecha === 'ano') && (
            <div>
              <label style={labelS}>Año</label>
              <input type="number" min="2000" max="2100" style={{ ...inp, width: 100 }}
                value={filtros.year}
                onChange={e => setF('year', e.target.value)} />
            </div>
          )}
          {modoFecha === 'mes' && (
            <div>
              <label style={labelS}>Mes</label>
              <select style={{ ...inp, width: 140 }} value={filtros.month}
                onChange={e => setF('month', e.target.value)}>
                {MESES.map(m => <option key={m.v} value={m.v}>{m.n}</option>)}
              </select>
            </div>
          )}

          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={consultar} disabled={loading}>
              {loading ? 'Consultando...' : '🔍 Consultar'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={exportExcel}
              disabled={!data?.articulos?.length}>
              ⇓ Excel
            </button>
          </div>
        </div>
      </div>

      {/* ── Resumen + acciones ── */}
      {data && (
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 10, padding: '10px 14px', fontSize: 12, color: '#94a3b8',
          background: esConsumo ? 'rgba(251,146,60,0.08)' : 'rgba(15,27,45,0.35)',
          border: `1px solid ${esConsumo ? 'rgba(251,146,60,0.25)' : 'rgba(255,255,255,0.05)'}`,
          borderRadius: 8, gap: 16, flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <div>
              <strong style={{ color: '#e2e8f0' }}>{data.total_articulos}</strong> artículo(s)
              {data.fecha_desde && <> · desde <strong style={{ color: '#e2e8f0' }}>{data.fecha_desde}</strong></>}
              {data.fecha_hasta && <> · hasta <strong style={{ color: '#e2e8f0' }}>{data.fecha_hasta}</strong></>}
            </div>
            {esConsumo && data.totales && (
              <div style={{ display: 'flex', gap: 14, paddingLeft: 14, borderLeft: '1px solid rgba(251,146,60,0.25)' }}>
                <div>
                  <div style={{ fontSize: 9.5, color: '#fdba74', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Total Consumido</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fb923c', fontFamily: 'monospace' }}>
                    {fmtQ(data.totales.cantidad_salida)} u
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9.5, color: '#fdba74', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Valor Consumido</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#fb923c', fontFamily: 'monospace' }}>
                    S/ {fmt(data.totales.valor_salida, 2)}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={expandirTodos}>Expandir todos</button>
            <button className="btn btn-secondary btn-sm" onClick={contraerTodos}>Contraer todos</button>
          </div>
        </div>
      )}

      {/* ── Lista de artículos ── */}
      {loading ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Calculando kardex...</div>
      ) : !data ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Configure filtros y presione Consultar.</div>
      ) : data.articulos.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>
          Sin artículos ni movimientos para los filtros seleccionados.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {data.articulos.map(a => {
            const abierto = !!expandidos[a.id_articulo];
            const sf = a.saldo_final  || { saldo_cant: 0, saldo_cu: 0, saldo_total: 0 };
            const si = a.saldo_inicial|| { saldo_cant: 0, saldo_cu: 0, saldo_total: 0 };
            const totConsumoCant  = (a.movimientos || []).reduce((s, m) => s + (parseFloat(m.cant_out) || 0), 0);
            const totConsumoValor = (a.movimientos || []).reduce((s, m) => s + (parseFloat(m.total_out) || 0), 0);
            return (
              <div key={a.id_articulo} style={{
                border: '1px solid rgba(255,255,255,0.07)', borderRadius: 10, overflow: 'hidden',
                background: 'rgba(15,27,45,0.4)',
              }}>
                {/* Cabecera del artículo */}
                <button type="button" onClick={() => toggleArt(a.id_articulo)}
                  style={{
                    width: '100%', padding: '12px 16px', cursor: 'pointer',
                    background: 'linear-gradient(90deg, rgba(15,27,45,0.8), rgba(15,27,45,0.4))',
                    border: 'none', borderLeft: '3px solid #60a5fa',
                    color: '#e2e8f0', textAlign: 'left', display: 'flex',
                    alignItems: 'center', gap: 12,
                  }}>
                  <span style={{ fontSize: 12, color: '#60a5fa', fontWeight: 700,
                    width: 14, textAlign: 'center' }}>{abierto ? '▾' : '▸'}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, padding: '2px 8px',
                    background: 'rgba(96,165,250,0.14)', color: '#93c5fd', borderRadius: 5,
                    fontWeight: 700 }}>{a.codigo_interno || '—'}</span>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>{a.descripcion || '—'}</span>
                  {a.unidad_descripcion && <span style={{ fontSize: 11, color: '#64748b' }}>· {a.unidad_descripcion}</span>}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 18, alignItems: 'center' }}>
                    {esConsumo ? (<>
                      <Metric label="Cant. Consumida" value={fmtQ(totConsumoCant)}  color="#fb923c" />
                      <Metric label="Valor Consumido" value={fmt(totConsumoValor, 2)} color="#fb923c" />
                      <Metric label="Saldo Actual"    value={fmtQ(sf.saldo_cant)}   color="#e2e8f0" />
                      <Metric label="Movimientos"     value={(a.movimientos || []).length} color="#94a3b8" />
                    </>) : (<>
                      <Metric label="Saldo"         value={fmtQ(sf.saldo_cant)} color="#e2e8f0" />
                      <Metric label="CU Promedio"   value={fmt(sf.saldo_cu, 4)} color="#93c5fd" />
                      <Metric label="Valor Total"   value={fmt(sf.saldo_total, 2)} color="#22c55e" />
                      <Metric label="Movimientos"   value={(a.movimientos || []).length} color="#94a3b8" />
                    </>)}
                  </div>
                </button>

                {/* Detalle expandido */}
                {abierto && (
                  <div style={{ overflowX: 'auto', background: 'rgba(0,0,0,0.15)' }}>
                    <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'separate', borderSpacing: 0, minWidth: 1400 }}>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                        {/* Fila 1: grupos */}
                        <tr>
                          <th colSpan={9} style={thGroup(null)}></th>
                          {!esConsumo && (
                            <th colSpan={3} style={thGroup('ingreso')}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={dotStyle('#22c55e')} /> INGRESOS
                              </span>
                            </th>
                          )}
                          <th colSpan={3} style={thGroup('salida')}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={dotStyle('#fb923c')} /> {esConsumo ? 'CONSUMO' : 'SALIDAS'}
                            </span>
                          </th>
                          <th colSpan={3} style={thGroup('saldo')}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={dotStyle('#60a5fa')} /> SALDOS
                            </span>
                          </th>
                        </tr>
                        {/* Fila 2: columnas */}
                        <tr style={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', fontSize: 10, background: 'rgba(15,27,45,0.95)' }}>
                          <th style={thCSS}>Fecha</th>
                          <th style={thCSS}>Mov.</th>
                          <th style={{ ...thCSS, textAlign: 'left' }}>Descripción Mov.</th>
                          <th style={thCSS}>Documento</th>
                          <th style={{ ...thCSS, textAlign: 'left' }}>Sede</th>
                          <th style={{ ...thCSS, textAlign: 'left' }}>Almacén</th>
                          <th style={{ ...thCSS, textAlign: 'center' }}>Ref. 1</th>
                          <th style={{ ...thCSS, textAlign: 'center' }}>Ref. 2</th>
                          <th style={{ ...thCSS, textAlign: 'left', borderRight: BDR_GROUP }}>C. Costo</th>

                          {!esConsumo && (<>
                            <th style={{ ...thCSS, ...thIn,  textAlign: 'right' }}>Cant.</th>
                            <th style={{ ...thCSS, ...thIn,  textAlign: 'right' }}>C. Unit.</th>
                            <th style={{ ...thCSS, ...thIn,  textAlign: 'right', borderRight: BDR_GROUP }}>Total</th>
                          </>)}

                          <th style={{ ...thCSS, ...thOut, textAlign: 'right' }}>Cant.</th>
                          <th style={{ ...thCSS, ...thOut, textAlign: 'right' }}>C. Unit.</th>
                          <th style={{ ...thCSS, ...thOut, textAlign: 'right', borderRight: BDR_GROUP }}>Total</th>

                          <th style={{ ...thCSS, ...thBal, textAlign: 'right' }}>Cant.</th>
                          <th style={{ ...thCSS, ...thBal, textAlign: 'right' }}>C. Unit.</th>
                          <th style={{ ...thCSS, ...thBal, textAlign: 'right' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {/* Saldo inicial */}
                        <tr style={{ background: 'rgba(148,163,184,0.07)', fontWeight: 600 }}>
                          <td colSpan={9} style={{ ...tdCSS, color: '#94a3b8', fontStyle: 'italic', borderRight: BDR_GROUP }}>
                            Saldo inicial {data.fecha_desde ? `al ${data.fecha_desde}` : ''}
                          </td>
                          {!esConsumo && <td colSpan={3} style={{ ...tdCSS, borderRight: BDR_GROUP, ...tdIn }}></td>}
                          <td colSpan={3} style={{ ...tdCSS, borderRight: BDR_GROUP, ...tdOut }}></td>
                          <td style={{ ...tdCSS, textAlign: 'right', ...tdBal }}>{fmtQ(si.saldo_cant)}</td>
                          <td style={{ ...tdCSS, textAlign: 'right', ...tdBal }}>{fmt(si.saldo_cu, 4)}</td>
                          <td style={{ ...tdCSS, textAlign: 'right', ...tdBal }}>{fmt(si.saldo_total, 2)}</td>
                        </tr>
                        {(a.movimientos || []).length === 0 ? (
                          <tr><td colSpan={esConsumo ? 15 : 18} style={{ ...tdCSS, textAlign: 'center', color: '#64748b', padding: 20, fontStyle: 'italic' }}>
                            {esConsumo ? 'Sin consumos (KS) en el período' : 'Sin movimientos en el período'}
                          </td></tr>
                        ) : (a.movimientos || []).map((m, i) => {
                          const tdc = TIPO_DOC_COLOR[m.tipo_doc] || {};
                          const isIn  = m.cant_in  && m.cant_in > 0;
                          const isOut = m.cant_out && m.cant_out > 0;
                          const ref1 = [m.cod_ref1, m.num_doc_ref1].filter(Boolean).join(' · ');
                          const ref2 = [m.cod_ref2, m.num_doc_ref2].filter(Boolean).join(' · ');
                          return (
                            <tr key={i} style={{
                              borderTop: '1px solid rgba(255,255,255,0.04)',
                              background: i % 2 ? 'rgba(255,255,255,0.015)' : 'transparent',
                            }}>
                              <td style={tdCSS}>{fmtFchFull(m.fecha_creacion || m.fecha)}</td>
                              <td style={{ ...tdCSS, textAlign: 'center', fontFamily: 'monospace', fontWeight: 700, color: '#94a3b8' }}>
                                {m.cod_mov || '—'}
                              </td>
                              <td style={tdCSS}>{m.desc_mov || '—'}</td>
                              {/* Documento: badge tipo + número formateado */}
                              <td style={{ ...tdCSS, textAlign: 'center', whiteSpace: 'nowrap' }}>
                                <span style={{
                                  display: 'inline-block', padding: '2px 8px', borderRadius: 5,
                                  fontWeight: 700, fontSize: 10, marginRight: 5,
                                  color: tdc.c, background: tdc.bg, border: `1px solid ${tdc.border}`,
                                }}>{m.tipo_doc}</span>
                                <span style={{ fontFamily: 'monospace', fontWeight: 700, color: '#e2e8f0' }}>
                                  {m.num_doc != null ? String(m.num_doc).padStart(6, '0') : '—'}
                                </span>
                              </td>
                              <td style={tdCSS}>{m.sucursal_nombre || '—'}</td>
                              <td style={tdCSS}>{m.almacen_nombre || '—'}</td>
                              <td style={{ ...tdCSS, textAlign: 'center', fontFamily: 'monospace', fontSize: 10.5 }}>{ref1 || '—'}</td>
                              <td style={{ ...tdCSS, textAlign: 'center', fontFamily: 'monospace', fontSize: 10.5 }}>{ref2 || '—'}</td>
                              <td style={{ ...tdCSS, borderRight: BDR_GROUP }}>{m.centro_costo || '—'}</td>

                              {/* INGRESOS */}
                              {!esConsumo && (<>
                                <td style={{ ...tdCSS, ...tdIn, textAlign: 'right', color: isIn ? '#4ade80' : '#475569', fontWeight: isIn ? 700 : 400 }}>
                                  {isIn ? fmtQ(m.cant_in) : '—'}
                                </td>
                                <td style={{ ...tdCSS, ...tdIn, textAlign: 'right', color: isIn ? '#86efac' : '#475569' }}>
                                  {m.cu_in != null ? fmt(m.cu_in, 4) : '—'}
                                </td>
                                <td style={{ ...tdCSS, ...tdIn, textAlign: 'right', borderRight: BDR_GROUP, color: isIn ? '#4ade80' : '#475569', fontWeight: isIn ? 700 : 400 }}>
                                  {m.total_in != null ? fmt(m.total_in, 2) : '—'}
                                </td>
                              </>)}

                              {/* SALIDAS */}
                              <td style={{ ...tdCSS, ...tdOut, textAlign: 'right', color: isOut ? '#fb923c' : '#475569', fontWeight: isOut ? 700 : 400 }}>
                                {isOut ? fmtQ(m.cant_out) : '—'}
                              </td>
                              <td style={{ ...tdCSS, ...tdOut, textAlign: 'right', color: isOut ? '#fdba74' : '#475569' }}>
                                {m.cu_out != null ? fmt(m.cu_out, 4) : '—'}
                              </td>
                              <td style={{ ...tdCSS, ...tdOut, textAlign: 'right', borderRight: BDR_GROUP, color: isOut ? '#fb923c' : '#475569', fontWeight: isOut ? 700 : 400 }}>
                                {m.total_out != null ? fmt(m.total_out, 2) : '—'}
                              </td>

                              {/* SALDOS */}
                              <td style={{ ...tdCSS, ...tdBal, textAlign: 'right', fontWeight: 700 }}>{fmtQ(m.saldo_cant)}</td>
                              <td style={{ ...tdCSS, ...tdBal, textAlign: 'right' }}>{fmt(m.saldo_cu, 4)}</td>
                              <td style={{ ...tdCSS, ...tdBal, textAlign: 'right', fontWeight: 700 }}>{fmt(m.saldo_total, 2)}</td>
                            </tr>
                          );
                        })}
                        {/* Saldo final */}
                        <tr style={{ background: 'rgba(34,197,94,0.08)', fontWeight: 700, borderTop: '2px solid rgba(34,197,94,0.3)' }}>
                          <td colSpan={9} style={{ ...tdCSS, color: '#22c55e', fontStyle: 'italic', borderRight: BDR_GROUP }}>
                            Saldo final {data.fecha_hasta ? `al ${data.fecha_hasta}` : ''}
                          </td>
                          {!esConsumo && <td colSpan={3} style={{ ...tdCSS, borderRight: BDR_GROUP }}></td>}
                          <td colSpan={3} style={{ ...tdCSS, borderRight: BDR_GROUP }}></td>
                          <td style={{ ...tdCSS, textAlign: 'right', color: '#22c55e' }}>{fmtQ(sf.saldo_cant)}</td>
                          <td style={{ ...tdCSS, textAlign: 'right', color: '#93c5fd' }}>{fmt(sf.saldo_cu, 4)}</td>
                          <td style={{ ...tdCSS, textAlign: 'right', color: '#22c55e' }}>{fmt(sf.saldo_total, 2)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Layout>
  );
}

const Metric = ({ label, value, color }) => (
  <div style={{ textAlign: 'right', minWidth: 70 }}>
    <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 700, color, fontFamily: 'monospace' }}>{value}</div>
  </div>
);

const thCSS = { padding: '8px 8px', textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.06)' };
const tdCSS = { padding: '6px 8px', color: '#cbd5e1', whiteSpace: 'nowrap', fontSize: 11.5, fontVariantNumeric: 'tabular-nums' };

// Separador vertical entre grupos (Ingresos / Salidas / Saldos)
const BDR_GROUP = '2px solid rgba(255,255,255,0.12)';

// Fondos sutiles por columna-grupo (pintan todas las celdas, cabecera y cuerpo)
const tdIn  = { background: 'rgba(34,197,94,0.05)' };
const tdOut = { background: 'rgba(251,146,60,0.05)' };
const tdBal = { background: 'rgba(96,165,250,0.08)' };

const thIn  = { background: 'rgba(34,197,94,0.10)',  color: '#86efac', borderBottom: '2px solid rgba(34,197,94,0.35)' };
const thOut = { background: 'rgba(251,146,60,0.10)', color: '#fdba74', borderBottom: '2px solid rgba(251,146,60,0.35)' };
const thBal = { background: 'rgba(96,165,250,0.12)', color: '#93c5fd', borderBottom: '2px solid rgba(96,165,250,0.35)' };

// Estilo de la cabecera de grupo (la fila con INGRESOS / SALIDAS / SALDOS)
const thGroup = (kind) => ({
  padding: '8px 10px',
  textAlign: 'center',
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 1,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  background:
    kind === 'ingreso' ? 'linear-gradient(180deg, rgba(34,197,94,0.20), rgba(34,197,94,0.05))' :
    kind === 'salida'  ? 'linear-gradient(180deg, rgba(251,146,60,0.20), rgba(251,146,60,0.05))' :
    kind === 'saldo'   ? 'linear-gradient(180deg, rgba(96,165,250,0.25), rgba(96,165,250,0.08))' :
    'rgba(15,27,45,0.95)',
  color:
    kind === 'ingreso' ? '#86efac' :
    kind === 'salida'  ? '#fdba74' :
    kind === 'saldo'   ? '#93c5fd' : 'transparent',
  borderRight: kind ? BDR_GROUP : 'none',
  borderBottom: kind ? `2px solid ${
    kind === 'ingreso' ? 'rgba(34,197,94,0.35)' :
    kind === 'salida'  ? 'rgba(251,146,60,0.35)' :
    'rgba(96,165,250,0.35)'
  }` : 'none',
});

const dotStyle = (c) => ({
  display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
  background: c, boxShadow: `0 0 6px ${c}99`,
});

// ══════════════════════════════════════════════════════════════════
// Excel profesional — helpers
// ══════════════════════════════════════════════════════════════════

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

// Paleta
const COLOR = {
  navy:     '0F1B2D',
  navyDeep: '1E3A5F',
  accent:   '3B82F6',
  green:    '22C55E',
  greenBg:  'DCFCE7',
  orange:   'FB923C',
  orangeBg: 'FFEDD5',
  blue:     '60A5FA',
  blueBg:   'DBEAFE',
  zebra:    'F5F8FC',
  grayBg:   'E2E8F0',
  text:     '1A1A2E',
  subtext:  '475569',
};

const thinBorder = (argb = 'CFD8DC') => ({
  top:    { style: 'hair', color: { argb } },
  bottom: { style: 'hair', color: { argb } },
  left:   { style: 'hair', color: { argb } },
  right:  { style: 'hair', color: { argb } },
});

const headerFont = { bold: true, color: { argb: 'FFFFFF' }, size: 10.5, name: 'Calibri' };
const headerAlign = { horizontal: 'center', vertical: 'middle', wrapText: true };

const fill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

const fmtFchFullX = (d) => {
  if (!d) return '';
  const s = String(d);
  const [date, timeRaw] = s.split('T');
  const time = (timeRaw || '').slice(0, 5);
  return time ? `${date} ${time}` : date;
};

const periodoTexto = (data) => {
  if (data.fecha_desde && data.fecha_hasta) return `Del ${data.fecha_desde} al ${data.fecha_hasta}`;
  if (data.fecha_desde) return `Desde ${data.fecha_desde}`;
  if (data.fecha_hasta) return `Hasta ${data.fecha_hasta}`;
  return 'Todos los períodos';
};

// ── Reporte de Consumo ──
async function _buildConsumoSheet(wb, data) {
  const ws = wb.addWorksheet('Consumo', {
    properties: { tabColor: { argb: COLOR.orange } },
    views: [{ state: 'frozen', ySplit: 5, xSplit: 3 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  const cols = [
    { header: 'Código',        key: 'codigo',      width: 14, align: 'left'  },
    { header: 'Descripción',   key: 'descripcion', width: 40, align: 'left'  },
    { header: 'Unidad',        key: 'unidad',      width: 10, align: 'center'},
    { header: 'Grupo',         key: 'grupo',       width: 16, align: 'left'  },
    { header: 'Familia',       key: 'familia',     width: 16, align: 'left'  },
    { header: 'Línea',         key: 'linea',       width: 16, align: 'left'  },
    { header: 'Marca',         key: 'marca',       width: 16, align: 'left'  },
    { header: 'Modelo',        key: 'modelo',      width: 16, align: 'left'  },
    { header: 'Fecha',         key: 'fecha',       width: 17, align: 'center'},
    { header: 'Cód. Mov.',     key: 'cod_mov',     width: 10, align: 'center'},
    { header: 'Desc. Mov.',    key: 'desc_mov',    width: 18, align: 'left'  },
    { header: 'Doc',           key: 'doc',         width: 6,  align: 'center'},
    { header: 'N° Doc',        key: 'num_doc',     width: 11, align: 'center'},
    { header: 'Sede',          key: 'sede',        width: 14, align: 'left'  },
    { header: 'Almacén',       key: 'almacen',     width: 18, align: 'left'  },
    { header: 'Ref. 1',        key: 'ref1',        width: 16, align: 'center'},
    { header: 'Ref. 2',        key: 'ref2',        width: 16, align: 'center'},
    { header: 'C. Costo',      key: 'cc',          width: 18, align: 'left'  },
    // CONSUMO
    { header: 'Cantidad',      key: 'consumo',     width: 13, align: 'right', numFmt: '#,##0.0000', group: 'consumo' },
    { header: 'C. Unitario',   key: 'cu',          width: 13, align: 'right', numFmt: '#,##0.0000', group: 'consumo' },
    { header: 'Total',         key: 'total',       width: 14, align: 'right', numFmt: '#,##0.00',   group: 'consumo' },
    // SALDOS
    { header: 'Saldo Cant.',   key: 'saldo_cant',  width: 13, align: 'right', numFmt: '#,##0.0000', group: 'saldo' },
    { header: 'CU Saldo',      key: 'saldo_cu',    width: 13, align: 'right', numFmt: '#,##0.0000', group: 'saldo' },
    { header: 'Total Saldo',   key: 'saldo_total', width: 14, align: 'right', numFmt: '#,##0.00',   group: 'saldo' },
  ];
  const N = cols.length;

  // Anchuras
  cols.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; if (c.numFmt) ws.getColumn(i + 1).numFmt = c.numFmt; });

  // Fila 1: título
  ws.mergeCells(1, 1, 1, N);
  const title = ws.getCell(1, 1);
  title.value = 'REPORTE DE CONSUMO';
  title.font = { bold: true, size: 16, color: { argb: 'FFFFFF' }, name: 'Calibri' };
  title.fill = fill(COLOR.navy);
  title.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(1).height = 34;

  // Fila 2: subtítulo
  ws.mergeCells(2, 1, 2, N);
  const sub = ws.getCell(2, 1);
  const totCant = data.totales?.cantidad_salida || 0;
  const totVal  = data.totales?.valor_salida || 0;
  sub.value = `Método: Promedio Ponderado    ·    ${periodoTexto(data)}    ·    ${data.total_articulos} artículo(s)    ·    Total consumido: ${totCant.toLocaleString('es-PE',{maximumFractionDigits:4})} u    ·    Valor: S/ ${totVal.toLocaleString('es-PE',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  sub.font = { italic: true, size: 10.5, color: { argb: COLOR.subtext }, name: 'Calibri' };
  sub.alignment = { horizontal: 'center', vertical: 'middle' };
  sub.fill = fill('F1F5F9');
  ws.getRow(2).height = 20;

  // Fila 3: vacía separadora
  ws.getRow(3).height = 6;

  // Fila 4: encabezado de grupo
  ws.mergeCells(4, 1, 4, 18);
  const grDoc = ws.getCell(4, 1);
  grDoc.value = 'DATOS DEL DOCUMENTO';
  grDoc.font = { bold: true, size: 11, color: { argb: 'FFFFFF' }, name: 'Calibri' };
  grDoc.fill = fill(COLOR.navyDeep);
  grDoc.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.mergeCells(4, 19, 4, 21);
  const grCon = ws.getCell(4, 19);
  grCon.value = 'CONSUMO';
  grCon.font = { bold: true, size: 11, color: { argb: 'FFFFFF' }, name: 'Calibri' };
  grCon.fill = fill(COLOR.orange);
  grCon.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.mergeCells(4, 22, 4, 24);
  const grSal = ws.getCell(4, 22);
  grSal.value = 'SALDOS';
  grSal.font = { bold: true, size: 11, color: { argb: 'FFFFFF' }, name: 'Calibri' };
  grSal.fill = fill(COLOR.blue);
  grSal.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(4).height = 22;

  // Fila 5: headers de columna
  cols.forEach((c, i) => {
    const cell = ws.getCell(5, i + 1);
    cell.value = c.header;
    cell.font = headerFont;
    cell.fill = fill(
      c.group === 'consumo' ? COLOR.orange :
      c.group === 'saldo'   ? COLOR.blue :
      COLOR.navy
    );
    cell.alignment = { ...headerAlign, horizontal: c.align === 'right' ? 'right' : 'center' };
    cell.border = thinBorder(COLOR.accent);
  });
  ws.getRow(5).height = 30;

  // Filas de datos
  let rowIdx = 6;
  let rowN = 0;
  data.articulos.forEach(a => {
    (a.movimientos || []).forEach(m => {
      const r = ws.getRow(rowIdx++);
      r.getCell(1).value  = a.codigo_interno || '';
      r.getCell(2).value  = a.descripcion || '';
      r.getCell(3).value  = a.unidad_descripcion || a.unidad_codigo || '';
      r.getCell(4).value  = a.grupo_descripcion || '';
      r.getCell(5).value  = a.familia_descripcion || '';
      r.getCell(6).value  = a.linea_descripcion || '';
      r.getCell(7).value  = a.marca_descripcion || '';
      r.getCell(8).value  = a.modelo_descripcion || '';
      r.getCell(9).value  = fmtFchFullX(m.fecha_creacion || m.fecha);
      r.getCell(10).value = m.cod_mov || '';
      r.getCell(11).value = m.desc_mov || '';
      r.getCell(12).value = m.tipo_doc || '';
      r.getCell(13).value = m.num_doc != null ? String(m.num_doc).padStart(6, '0') : '';
      r.getCell(14).value = m.sucursal_nombre || '';
      r.getCell(15).value = m.almacen_nombre || '';
      r.getCell(16).value = [m.cod_ref1, m.num_doc_ref1].filter(Boolean).join(' · ');
      r.getCell(17).value = [m.cod_ref2, m.num_doc_ref2].filter(Boolean).join(' · ');
      r.getCell(18).value = m.centro_costo || '';
      r.getCell(19).value = parseFloat(m.cant_out)   || 0;
      r.getCell(20).value = parseFloat(m.cu_out)     || 0;
      r.getCell(21).value = parseFloat(m.total_out)  || 0;
      r.getCell(22).value = parseFloat(m.saldo_cant) || 0;
      r.getCell(23).value = parseFloat(m.saldo_cu)   || 0;
      r.getCell(24).value = parseFloat(m.saldo_total)|| 0;

      // Estilos: zebra + tintes por grupo
      const zebra = rowN % 2 === 1;
      for (let c = 1; c <= N; c++) {
        const cell = r.getCell(c);
        cell.font = { size: 10, name: 'Calibri', color: { argb: COLOR.text } };
        cell.border = { bottom: { style: 'hair', color: { argb: 'E2E8F0' } } };
        cell.alignment = { horizontal: cols[c - 1].align || 'left', vertical: 'middle' };
        // Fondos
        if (c >= 19 && c <= 21)      cell.fill = fill(zebra ? 'FFF4E6' : COLOR.orangeBg);
        else if (c >= 22 && c <= 24) cell.fill = fill(zebra ? 'E6EFFC' : COLOR.blueBg);
        else if (zebra)              cell.fill = fill(COLOR.zebra);
      }
      // Código en mono
      r.getCell(1).font  = { size: 10, name: 'Consolas', color: { argb: COLOR.text } };
      r.getCell(10).font = { size: 10, name: 'Consolas', bold: true, color: { argb: COLOR.text } };
      r.getCell(13).font = { size: 10, name: 'Consolas', bold: true, color: { argb: COLOR.text } };
      r.getCell(12).font = { size: 10, name: 'Calibri', bold: true, color: { argb: COLOR.text } };

      r.height = 17;
      rowN++;
    });
  });

  // Fila de totales (siempre, aunque sea 0)
  const trow = ws.getRow(rowIdx++);
  ws.mergeCells(trow.number, 1, trow.number, 18);
  const label = trow.getCell(1);
  label.value = 'TOTAL GENERAL';
  label.font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
  label.fill = fill(COLOR.navyDeep);
  label.alignment = { horizontal: 'right', vertical: 'middle' };
  trow.getCell(19).value = data.totales?.cantidad_salida || 0;
  trow.getCell(21).value = data.totales?.valor_salida    || 0;
  for (let c = 19; c <= 21; c++) {
    trow.getCell(c).font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
    trow.getCell(c).fill = fill(COLOR.orange);
    trow.getCell(c).alignment = { horizontal: 'right', vertical: 'middle' };
    trow.getCell(c).numFmt = cols[c - 1].numFmt || '#,##0.00';
  }
  for (let c = 22; c <= 24; c++) {
    trow.getCell(c).fill = fill(COLOR.grayBg);
  }
  trow.height = 24;

  // Autofiltro en fila de headers
  ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: N } };
}

// ── Kardex con saldos inicial/final ──
async function _buildKardexSheet(wb, data) {
  const ws = wb.addWorksheet('Kardex', {
    properties: { tabColor: { argb: COLOR.blue } },
    views: [{ state: 'frozen', ySplit: 5, xSplit: 3 }],
    pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
  });

  const cols = [
    { header: 'Código',        key: 'codigo',      width: 14, align: 'left'  },
    { header: 'Descripción',   key: 'descripcion', width: 40, align: 'left'  },
    { header: 'Unidad',        key: 'unidad',      width: 10, align: 'center'},
    { header: 'Grupo',         key: 'grupo',       width: 16, align: 'left'  },
    { header: 'Familia',       key: 'familia',     width: 16, align: 'left'  },
    { header: 'Línea',         key: 'linea',       width: 16, align: 'left'  },
    { header: 'Marca',         key: 'marca',       width: 16, align: 'left'  },
    { header: 'Modelo',        key: 'modelo',      width: 16, align: 'left'  },
    { header: 'Fecha',         key: 'fecha',       width: 17, align: 'center'},
    { header: 'Cód. Mov.',     key: 'cod_mov',     width: 14, align: 'center'},
    { header: 'Desc. Mov.',    key: 'desc_mov',    width: 18, align: 'left'  },
    { header: 'Doc',           key: 'doc',         width: 6,  align: 'center'},
    { header: 'N° Doc',        key: 'num_doc',     width: 11, align: 'center'},
    { header: 'Sede',          key: 'sede',        width: 14, align: 'left'  },
    { header: 'Almacén',       key: 'almacen',     width: 18, align: 'left'  },
    { header: 'Ref. 1',        key: 'ref1',        width: 16, align: 'center'},
    { header: 'Ref. 2',        key: 'ref2',        width: 16, align: 'center'},
    { header: 'C. Costo',      key: 'cc',          width: 18, align: 'left'  },
    // INGRESOS
    { header: 'Cantidad',      key: 'in_cant',     width: 12, align: 'right', numFmt: '#,##0.0000', group: 'in' },
    { header: 'C. Unitario',   key: 'in_cu',       width: 12, align: 'right', numFmt: '#,##0.0000', group: 'in' },
    { header: 'Total',         key: 'in_total',    width: 13, align: 'right', numFmt: '#,##0.00',   group: 'in' },
    // SALIDAS
    { header: 'Cantidad',      key: 'out_cant',    width: 12, align: 'right', numFmt: '#,##0.0000', group: 'out' },
    { header: 'C. Unitario',   key: 'out_cu',      width: 12, align: 'right', numFmt: '#,##0.0000', group: 'out' },
    { header: 'Total',         key: 'out_total',   width: 13, align: 'right', numFmt: '#,##0.00',   group: 'out' },
    // SALDOS
    { header: 'Saldo Cant.',   key: 'saldo_cant',  width: 13, align: 'right', numFmt: '#,##0.0000', group: 'saldo' },
    { header: 'CU Saldo',      key: 'saldo_cu',    width: 13, align: 'right', numFmt: '#,##0.0000', group: 'saldo' },
    { header: 'Total Saldo',   key: 'saldo_total', width: 14, align: 'right', numFmt: '#,##0.00',   group: 'saldo' },
  ];
  const N = cols.length;
  cols.forEach((c, i) => { ws.getColumn(i + 1).width = c.width; if (c.numFmt) ws.getColumn(i + 1).numFmt = c.numFmt; });

  // Fila 1: título
  ws.mergeCells(1, 1, 1, N);
  const title = ws.getCell(1, 1);
  title.value = 'KARDEX VALORIZADO';
  title.font = { bold: true, size: 16, color: { argb: 'FFFFFF' }, name: 'Calibri' };
  title.fill = fill(COLOR.navy);
  title.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(1).height = 34;

  // Fila 2: subtítulo
  ws.mergeCells(2, 1, 2, N);
  const sub = ws.getCell(2, 1);
  sub.value = `Método: Promedio Ponderado    ·    ${periodoTexto(data)}    ·    ${data.total_articulos} artículo(s)`;
  sub.font = { italic: true, size: 10.5, color: { argb: COLOR.subtext } };
  sub.alignment = { horizontal: 'center', vertical: 'middle' };
  sub.fill = fill('F1F5F9');
  ws.getRow(2).height = 20;

  // Fila 3: vacía separadora
  ws.getRow(3).height = 6;

  // Fila 4: encabezado de grupo
  ws.mergeCells(4, 1, 4, 18);
  ws.getCell(4, 1).value = 'DATOS DEL DOCUMENTO';
  ws.getCell(4, 1).font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
  ws.getCell(4, 1).fill = fill(COLOR.navyDeep);
  ws.getCell(4, 1).alignment = { horizontal: 'center', vertical: 'middle' };
  ws.mergeCells(4, 19, 4, 21);
  ws.getCell(4, 19).value = 'INGRESOS';
  ws.getCell(4, 19).font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
  ws.getCell(4, 19).fill = fill(COLOR.green);
  ws.getCell(4, 19).alignment = { horizontal: 'center', vertical: 'middle' };
  ws.mergeCells(4, 22, 4, 24);
  ws.getCell(4, 22).value = 'SALIDAS';
  ws.getCell(4, 22).font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
  ws.getCell(4, 22).fill = fill(COLOR.orange);
  ws.getCell(4, 22).alignment = { horizontal: 'center', vertical: 'middle' };
  ws.mergeCells(4, 25, 4, 27);
  ws.getCell(4, 25).value = 'SALDOS';
  ws.getCell(4, 25).font = { bold: true, size: 11, color: { argb: 'FFFFFF' } };
  ws.getCell(4, 25).fill = fill(COLOR.blue);
  ws.getCell(4, 25).alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(4).height = 22;

  // Fila 5: headers de columna
  cols.forEach((c, i) => {
    const cell = ws.getCell(5, i + 1);
    cell.value = c.header;
    cell.font = headerFont;
    cell.fill = fill(
      c.group === 'in'    ? COLOR.green :
      c.group === 'out'   ? COLOR.orange :
      c.group === 'saldo' ? COLOR.blue :
      COLOR.navy
    );
    cell.alignment = { ...headerAlign, horizontal: c.align === 'right' ? 'right' : 'center' };
    cell.border = thinBorder(COLOR.accent);
  });
  ws.getRow(5).height = 30;

  // Datos por artículo
  let rowIdx = 6;
  data.articulos.forEach((a, artIdx) => {
    const baseInfo = [
      a.codigo_interno || '',
      a.descripcion || '',
      a.unidad_descripcion || a.unidad_codigo || '',
      a.grupo_descripcion || '',
      a.familia_descripcion || '',
      a.linea_descripcion || '',
      a.marca_descripcion || '',
      a.modelo_descripcion || '',
    ];

    // Fila SALDO INICIAL
    const rSI = ws.getRow(rowIdx++);
    baseInfo.forEach((v, i) => { rSI.getCell(i + 1).value = v; });
    rSI.getCell(10).value = 'SALDO INICIAL';
    rSI.getCell(25).value = a.saldo_inicial?.saldo_cant  || 0;
    rSI.getCell(26).value = a.saldo_inicial?.saldo_cu    || 0;
    rSI.getCell(27).value = a.saldo_inicial?.saldo_total || 0;
    for (let c = 1; c <= N; c++) {
      const cell = rSI.getCell(c);
      cell.font = { size: 10, bold: true, italic: true, color: { argb: COLOR.subtext } };
      cell.fill = fill(COLOR.grayBg);
      cell.border = { bottom: { style: 'hair', color: { argb: 'CBD5E1' } } };
      cell.alignment = { horizontal: cols[c - 1].align || 'left', vertical: 'middle' };
    }
    rSI.getCell(10).alignment = { horizontal: 'center', vertical: 'middle' };
    rSI.height = 18;

    // Filas de movimientos
    (a.movimientos || []).forEach((m, i) => {
      const r = ws.getRow(rowIdx++);
      baseInfo.forEach((v, ix) => { r.getCell(ix + 1).value = v; });
      r.getCell(9).value  = fmtFchFullX(m.fecha_creacion || m.fecha);
      r.getCell(10).value = m.cod_mov || '';
      r.getCell(11).value = m.desc_mov || '';
      r.getCell(12).value = m.tipo_doc || '';
      r.getCell(13).value = m.num_doc != null ? String(m.num_doc).padStart(6, '0') : '';
      r.getCell(14).value = m.sucursal_nombre || '';
      r.getCell(15).value = m.almacen_nombre || '';
      r.getCell(16).value = [m.cod_ref1, m.num_doc_ref1].filter(Boolean).join(' · ');
      r.getCell(17).value = [m.cod_ref2, m.num_doc_ref2].filter(Boolean).join(' · ');
      r.getCell(18).value = m.centro_costo || '';
      r.getCell(19).value = parseFloat(m.cant_in)    || 0;
      r.getCell(20).value = parseFloat(m.cu_in)      || 0;
      r.getCell(21).value = parseFloat(m.total_in)   || 0;
      r.getCell(22).value = parseFloat(m.cant_out)   || 0;
      r.getCell(23).value = parseFloat(m.cu_out)     || 0;
      r.getCell(24).value = parseFloat(m.total_out)  || 0;
      r.getCell(25).value = parseFloat(m.saldo_cant) || 0;
      r.getCell(26).value = parseFloat(m.saldo_cu)   || 0;
      r.getCell(27).value = parseFloat(m.saldo_total)|| 0;

      const zebra = i % 2 === 1;
      for (let c = 1; c <= N; c++) {
        const cell = r.getCell(c);
        cell.font = { size: 10, name: 'Calibri', color: { argb: COLOR.text } };
        cell.border = { bottom: { style: 'hair', color: { argb: 'E2E8F0' } } };
        cell.alignment = { horizontal: cols[c - 1].align || 'left', vertical: 'middle' };
        if      (c >= 19 && c <= 21) cell.fill = fill(zebra ? 'D9F7E2' : COLOR.greenBg);
        else if (c >= 22 && c <= 24) cell.fill = fill(zebra ? 'FFF4E6' : COLOR.orangeBg);
        else if (c >= 25 && c <= 27) cell.fill = fill(zebra ? 'E6EFFC' : COLOR.blueBg);
        else if (zebra)              cell.fill = fill(COLOR.zebra);
      }
      r.getCell(1).font  = { size: 10, name: 'Consolas', color: { argb: COLOR.text } };
      r.getCell(10).font = { size: 10, name: 'Consolas', bold: true, color: { argb: COLOR.text } };
      r.getCell(13).font = { size: 10, name: 'Consolas', bold: true, color: { argb: COLOR.text } };
      r.getCell(12).font = { size: 10, name: 'Calibri',  bold: true, color: { argb: COLOR.text } };
      r.height = 17;
    });

    // Fila SALDO FINAL
    const rSF = ws.getRow(rowIdx++);
    baseInfo.forEach((v, i) => { rSF.getCell(i + 1).value = v; });
    rSF.getCell(10).value = 'SALDO FINAL';
    rSF.getCell(25).value = a.saldo_final?.saldo_cant  || 0;
    rSF.getCell(26).value = a.saldo_final?.saldo_cu    || 0;
    rSF.getCell(27).value = a.saldo_final?.saldo_total || 0;
    for (let c = 1; c <= N; c++) {
      const cell = rSF.getCell(c);
      cell.font = { size: 10, bold: true, color: { argb: '166534' } };
      cell.fill = fill(COLOR.greenBg);
      cell.border = {
        top:    { style: 'medium', color: { argb: COLOR.green } },
        bottom: { style: 'medium', color: { argb: COLOR.green } },
      };
      cell.alignment = { horizontal: cols[c - 1].align || 'left', vertical: 'middle' };
    }
    rSF.getCell(10).alignment = { horizontal: 'center', vertical: 'middle' };
    rSF.height = 19;

    // Separador entre artículos: no agregamos fila vacía para no romper filtros;
    // el fondo verde del saldo final ya actúa como separador.
  });

  ws.autoFilter = { from: { row: 5, column: 1 }, to: { row: 5, column: N } };
}
